import React, { useEffect, useState, useCallback } from 'react';
import { theme } from '../theme';
// v0.17.1 — Lucide icons for status glyphs.
import { Check, AlertTriangle, X as XIcon, Info } from 'lucide-react';
// 2026-10-19 — the sign-in has ended and the shell is taking the person to sign
// in: a failure toast from the screen being left is the error-then-gone Brett
// reported (utils/sessionEnd).
import { signInEnded } from '../utils/sessionEnd';

// ---------------------------------------------------------------------------
// Toaster - module-level pub-sub, no React Context plumbing required.
//
// v0.9.3 - initial. Replaces alert() calls for user-facing failures.
//          Approach: a tiny window-level EventTarget that any module can
//          dispatch onto. <ToasterRoot/> mounts ONCE in App.jsx and
//          listens; consumers either call toast(msg, kind) directly or
//          use the useToaster() hook for symmetry with hook-style APIs.
//
//          Why pub-sub instead of Context: a single global bus means we
//          can fire toasts from non-React code (api.js error wrappers,
//          for instance) without needing the toast caller to be inside
//          the provider tree. Trade-off: there's no per-tree scoping,
//          but DropPin never needs that.
//
// Usage:
//   import { toast } from './components/Toaster';
//   toast('Saved', 'success');
//   toast('Failed: bad input', 'error');
//
//   // or via the hook (gives back the same toast fn — pure ergonomics):
//   const { toast } = useToaster();
//
// Kinds:   'info' | 'success' | 'warn' | 'error'   (default: 'info')
// Auto-dismiss after 4s; manual dismiss via the X.
// ---------------------------------------------------------------------------

const BUS = typeof window !== 'undefined' ? new EventTarget() : null;
const TOAST_EVT = 'droppin:toast';

export function toast(message, kind = 'info') {
  if (!BUS || !message) return;
  if ((kind === 'error' || kind === 'warn') && signInEnded()) return;
  BUS.dispatchEvent(new CustomEvent(TOAST_EVT, {
    detail: { message: String(message), kind, id: Date.now() + Math.random() },
  }));
}

export function useToaster() {
  return { toast };
}

// v0.17.1 — Lucide icons replace the text glyphs for visual consistency.
const KIND_STYLES = {
  info:    { bg: theme.bg, fg: theme.brand, border: theme.border, Icon: Info },
  success: { bg: theme.bg, fg: theme.success, border: theme.success, Icon: Check },
  warn:    { bg: theme.bg, fg: theme.warn, border: theme.warn, Icon: AlertTriangle },
  error:   { bg: theme.bg, fg: theme.danger, border: theme.danger, Icon: XIcon },
};

export default function ToasterRoot() {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    if (!BUS) return;
    function onToast(e) {
      const t = e.detail;
      if (!t) return;
      setToasts(prev => [...prev, t]);
      // Auto-dismiss after 4s.
      setTimeout(() => dismiss(t.id), 4000);
    }
    BUS.addEventListener(TOAST_EVT, onToast);
    return () => BUS.removeEventListener(TOAST_EVT, onToast);
  }, [dismiss]);

  if (!toasts.length) return null;

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      style={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        maxWidth: 360,
        pointerEvents: 'none', // children re-enable
      }}
    >
      {toasts.map(t => {
        const s = KIND_STYLES[t.kind] || KIND_STYLES.info;
        return (
          <div
            key={t.id}
            role="status"
            style={{
              pointerEvents: 'auto',
              background: s.bg,
              color: s.fg,
              border: `1px solid ${s.border}`,
              borderRadius: 8,
              padding: '10px 12px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              fontSize: 13,
              fontFamily: 'inherit',
              minWidth: 240,
              animation: 'droppinToastIn 0.18s ease-out',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                marginTop: 1,
                flexShrink: 0,
              }}
            ><s.Icon size={16} strokeWidth={2} /></span>
            <div style={{ flex: 1, lineHeight: 1.35 }}>{t.message}</div>
            <button
              onClick={() => dismiss(t.id)}
              title="Dismiss"
              style={{
                background: 'transparent',
                border: 'none',
                color: s.fg,
                fontSize: 16,
                lineHeight: 1,
                cursor: 'pointer',
                padding: 0,
                opacity: 0.6,
                fontFamily: 'inherit',
              }}
            >×</button>
          </div>
        );
      })}
      {/* Inline keyframe so we don't need a global stylesheet edit. */}
      <style>{`
        @keyframes droppinToastIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// Theme-token re-export so consumers can colour-check if they ever want to.
// (Not currently used, but kept for parity with the rest of the components
// folder which all re-export their tokens.)
export const TOASTER_KIND_STYLES = KIND_STYLES;
export const _theme = theme;
